import { useState } from 'react'
import './NewFailureDialog.css'

interface NewFailureDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: {
    title: string
    tag_name: string
    github_repository: string
    branch: string
    failing_changeset: string
    fixed_changeset: string
    test_file: string
  }) => Promise<void>
}

function NewFailureDialog({ open, onClose, onSubmit }: NewFailureDialogProps) {
  const [githubRepository, setGithubRepository] = useState('')
  const [branch, setBranch] = useState('main')
  const [failingChangeset, setFailingChangeset] = useState('')
  const [fixedChangeset, setFixedChangeset] = useState('')
  const [testFile, setTestFile] = useState('')
  const [title, setTitle] = useState('')
  const [tagName, setTagName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (!open) return null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    try {
      await onSubmit({
        title: title || `Failure: ${githubRepository.split('/').pop() || 'Untitled'}`,
        tag_name: tagName,
        github_repository: githubRepository,
        branch,
        failing_changeset: failingChangeset,
        fixed_changeset: fixedChangeset,
        test_file: testFile,
      })
      resetForm()
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  function resetForm() {
    setTitle('')
    setGithubRepository('')
    setBranch('main')
    setFailingChangeset('')
    setFixedChangeset('')
    setTestFile('')
    setTagName('')
  }

  function handleCancel() {
    resetForm()
    onClose()
  }

  return (
    <div data-testid="new-failure-dialog-overlay" className="dialog-overlay" onClick={handleCancel}>
      <div
        data-testid="new-failure-dialog"
        className="dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="dialog-title">New Failure</h2>

        <form onSubmit={handleSubmit} className="dialog-form">
          <div className="dialog-field">
            <label className="dialog-label" htmlFor="failure-title">
              Title:
            </label>
            <input
              id="failure-title"
              data-testid="failure-title-input"
              type="text"
              className="dialog-input"
              placeholder="e.g., Authentication API 500 Error"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="dialog-field">
            <label className="dialog-label" htmlFor="failure-tag">
              Tag Name:
            </label>
            <input
              id="failure-tag"
              data-testid="failure-tag-input"
              type="text"
              className="dialog-input"
              placeholder="e.g., Code Generation Errors"
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
            />
          </div>

          <div className="dialog-field">
            <label className="dialog-label" htmlFor="failure-github-repo">
              GitHub Repository:
            </label>
            <input
              id="failure-github-repo"
              data-testid="failure-github-repo-input"
              type="text"
              className="dialog-input"
              placeholder="e.g., https://github.com/acme/web-app"
              value={githubRepository}
              onChange={(e) => setGithubRepository(e.target.value)}
              required
            />
          </div>

          <div className="dialog-field">
            <label className="dialog-label" htmlFor="failure-branch">
              Branch:
            </label>
            <input
              id="failure-branch"
              data-testid="failure-branch-input"
              type="text"
              className="dialog-input"
              placeholder="e.g., main"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              required
            />
          </div>

          <div className="dialog-field">
            <label className="dialog-label" htmlFor="failure-failing-changeset">
              Failing Changeset (commit SHA):
            </label>
            <input
              id="failure-failing-changeset"
              data-testid="failure-failing-changeset-input"
              type="text"
              className="dialog-input"
              placeholder="e.g., a1b2c3d4e5f6"
              value={failingChangeset}
              onChange={(e) => setFailingChangeset(e.target.value)}
              required
            />
          </div>

          <div className="dialog-field">
            <label className="dialog-label" htmlFor="failure-fixed-changeset">
              Fixed Changeset (commit SHA):
            </label>
            <input
              id="failure-fixed-changeset"
              data-testid="failure-fixed-changeset-input"
              type="text"
              className="dialog-input"
              placeholder="e.g., f6e5d4c3b2a1"
              value={fixedChangeset}
              onChange={(e) => setFixedChangeset(e.target.value)}
              required
            />
          </div>

          <div className="dialog-field">
            <label className="dialog-label" htmlFor="failure-test-file">
              Test File:
            </label>
            <input
              id="failure-test-file"
              data-testid="failure-test-file-input"
              type="text"
              className="dialog-input"
              placeholder="e.g., tests/auth.spec.ts"
              value={testFile}
              onChange={(e) => setTestFile(e.target.value)}
              required
            />
          </div>

          <p className="immutability-notice">
            Failures are immutable once created. Ensure information is correct.
          </p>

          <div className="dialog-actions">
            <button
              type="button"
              data-testid="cancel-failure-btn"
              className="dialog-btn-cancel"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="create-failure-btn"
              className="dialog-btn-submit"
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Failure'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default NewFailureDialog
